/**
 * @file Typed API client factory (F1.3, F1.9).
 *
 * Wraps openapi-fetch with the base URL from runtime configuration, sends the
 * session cookie, and stamps every request with an X-Request-Id so a browser
 * action can be followed through the API and its worker jobs. Endpoint types
 * come from src/api/generated/schema.d.ts, regenerated with `npm run
 * api:generate`; hand-written fetch calls for known endpoints are not allowed
 * (CLAUDE.md section 3).
 */
import createClient from 'openapi-fetch';

import type { paths } from './generated/schema';

/** Typed client over every path in the OpenAPI document. */
export type ApiClient = ReturnType<typeof createClient<paths>>;

/** Header the API reads and echoes for correlation (F1.9). */
export const REQUEST_ID_HEADER = 'x-request-id';

/**
 * The request ID of the most recent API call. Held in the module rather than
 * in state because the error reporter (lib/observability) needs it after a
 * render has already failed, when no component tree is left to read from.
 */
let lastRequestId: string | null = null;

/**
 * The X-Request-Id of the most recent API call, or null before the first one.
 * Used to correlate a client-side error report with the API and worker logs
 * for the same action (F1.9).
 */
export function getLastRequestId(): string | null {
  return lastRequestId;
}

/** Clears the correlation ID. Tests only. */
export function resetLastRequestIdForTests(): void {
  lastRequestId = null;
}

/** Mints a request ID. UUIDs where available, otherwise a random token of the same shape. */
export function newRequestId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `req-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Creates the client for the given API base URL. */
export function createApiClient(baseUrl: string): ApiClient {
  const client = createClient<paths>({ baseUrl, credentials: 'include' });
  client.use({
    onRequest({ request }) {
      const requestId = newRequestId();
      request.headers.set(REQUEST_ID_HEADER, requestId);
      lastRequestId = requestId;
      return request;
    },
  });
  return client;
}
